import React, { useState, useContext, useEffect, useMemo } from 'react';
import { WebsiteContext } from '../../context/WebsiteProvider';
import HistoryWithArrow from '../../components/display/HistoryWithArrow';
import Timeline from '../../components/display/Timeline';
import YearTimeLine from '../../components/display/YearTimeLine';
import Changes from '../../components/display/Changes';
import { MdArrowBack } from 'react-icons/md';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

const getWeekOfMonth = (date) => {
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const pastDays = date.getDate() + firstDayOfMonth.getDay() - 1;
  return Math.ceil(pastDays / 7);
};

const WebsiteHistory = ({ websiteId }) => {
  const { getActivityDetails, activity } = useContext(WebsiteContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [isTimeline, setIsTimeline] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [error, setError] = useState(null);

  const transformedData = useMemo(() => {
    return Array.isArray(activity)
      ? activity.map((entry, index) => ({
        id: index,
        name: entry.user.username,
        action: entry.changes
          .map((change) => change.path.join(' -> '))
          .join(', '),
        type: entry.changes[0]?.type || 'Default',
        date: entry.date,
        description: entry.description,
        versionNumber: entry.versionNumber,
      }))
      : [];
  }, [activity]);

  useEffect(() => {
    const filtered = transformedData.filter(
      (event) =>
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (selectedName === '' || event.name === selectedName)
    );
    setFilteredEvents(filtered);
  }, [searchTerm, selectedName, transformedData]);

  const switchToTimeline = () => {
    setIsTimeline(!isTimeline);
  };

  const uniqueNames = Array.from(
    new Set(transformedData.map((event) => event.name))
  );

  const handleFilter = (year, month, week) => {
    const filtered = transformedData.filter((event) => {
      const eventDate = new Date(event.date);
      if (year !== null && eventDate.getFullYear() !== year) return false;
      if (month !== null && eventDate.getMonth() !== month) return false;
      if (week !== null && getWeekOfMonth(eventDate) !== week) return false;
      return true;
    });
    setFilteredEvents(filtered);
  };

  const handleNameChange = (e) => {
    setSelectedName(e.target.value);
    setSearchTerm('');
  };

  const handleItemClick = async (event, retryCount = 0) => {
    try {
      setError(null);
      const details = await getActivityDetails(websiteId, event.versionNumber);
      if (details && typeof details === 'object' && Object.keys(details).length > 0) {
        setSelectedEvent({
          ...details,
          changes: activity.find(a => a.versionNumber === event.versionNumber)?.changes || []
        });
      } else {
        console.error("Invalid getActivityDetails response:", details);
        if (retryCount < 2) {
          return handleItemClick(event, retryCount + 1);
        }
        setError(`Failed to load details for version ${event.versionNumber}`);
        setSelectedEvent({
          vendor: "Unknown",
          dba: "Unknown",
          domainName: "",
          status: "",
          startDate: "",
          termDate: "",
          memberNumber: "",
          product: "None",
          mlsId: "",
          firstName: "",
          lastName: "",
          office: "",
          mlsStatus: "",
          licenseNumber: "",
          broker: { firstName: "", lastName: "" },
          changes: activity.find(a => a.versionNumber === event.versionNumber)?.changes || []
        });
      }
    } catch (err) {
      console.error("getActivityDetails error:", err);
      if (retryCount < 2) {
        return handleItemClick(event, retryCount + 1);
      }
      setError(`Error loading details: ${err.message}`);
      setSelectedEvent({
        vendor: "Unknown",
        dba: "Unknown",
        domainName: "",
        status: "",
        startDate: "",
        termDate: "",
        memberNumber: "",
        product: "None",
        mlsId: "",
        firstName: "",
        lastName: "",
        office: "",
        mlsStatus: "",
        licenseNumber: "",
        broker: { firstName: "", lastName: "" },
        changes: activity.find(a => a.versionNumber === event.versionNumber)?.changes || []
      });
    }
  };

  const handleBackClick = () => {
    setSelectedEvent(null);
    setError(null);
  };

  return (
    <div className="relative mx-10 bg-white pt-10 text-white dark:bg-stone-900">
      <TransitionGroup>
        {selectedEvent ? (
          <CSSTransition
            key="changes"
            timeout={300}
            classNames="drawer"
            unmountOnExit
          >
            <div className="drawer-container">
              <button
                className="flex items-center text-xs font-bold text-blue-500 dark:text-indigo-500"
                onClick={handleBackClick}
              >
                <MdArrowBack className="mr-2" /> Back
              </button>
              {error ? (
                <div className="text-red-500">{error}</div>
              ) : (
                <Changes
                  form={selectedEvent}
                  changes={selectedEvent.changes}
                  darkMode={false}
                />
              )}
            </div>
          </CSSTransition>
        ) : (
          <CSSTransition
            key="history"
            timeout={300}
            classNames="transition"
            unmountOnExit
          >
            <div>
              {error && <div className="text-red-500 mb-4">{error}</div>}
              {!isTimeline && (
                <>
                  <div className="mb-4 flex">
                    <input
                      type="text"
                      placeholder="Search..."
                      className="w-3/4 rounded-md border border-gray-300 bg-gray-100 p-2 text-gray-600 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select
                      className="ml-2 w-1/4 cursor-pointer rounded-md border border-gray-300 bg-gray-100 p-2 text-gray-600 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                      value={selectedName}
                      onChange={handleNameChange}
                    >
                      <option value="">All Users</option>
                      {uniqueNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {isTimeline ? (
                <div className="">
                  <Timeline
                    events={transformedData}
                    onFilter={handleFilter}
                    getWeekOfMonth={getWeekOfMonth}
                  />
                  <YearTimeLine
                    events={transformedData}
                    onFilter={handleFilter}
                    getWeekOfMonth={getWeekOfMonth}
                  />
                  <button
                    className="mb-4 ml-2 mt-5 text-xs font-bold text-blue-500 dark:text-indigo-500"
                    onClick={switchToTimeline}
                  >
                    Switch to List
                  </button>
                </div>
              ) : (
                <div className="mt-0.5 px-2">
                  <HistoryWithArrow
                    events={filteredEvents}
                    onItemClick={handleItemClick}
                  />
                </div>
              )}
            </div>
          </CSSTransition>
        )}
      </TransitionGroup>
    </div>
  );
};

export default WebsiteHistory;